/**
 * The presentation half of the inbox: skeleton while the first page loads,
 * the empty state when there is nothing, and otherwise day-grouped rows
 * newest first. Never fetches, never filters, never sorts.
 */
import { Skeleton } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { SectionEmptyState } from "../../shell/SectionScreen";
import { groupByDay, resolveViewerTimeZone } from "./inboxTime";
import {
  NotificationRow,
  type typeNotificationRowItem,
} from "./NotificationRow";

export type typeNotificationInboxListProps = {
  items: readonly typeNotificationRowItem[];
  isFirstPagePending: boolean;
  now: Date;
  onOpen: (payload: { inboxItemId: string }) => void;
};

export const NotificationInboxList = ({
  items,
  isFirstPagePending,
  now,
  onOpen,
}: typeNotificationInboxListProps): ReactNode => {
  if (isFirstPagePending) {
    return (
      <div
        data-testid="notification-inbox-skeleton"
        className="flex flex-col gap-3"
      >
        <Skeleton shape="card" />
        <Skeleton shape="card" />
        <Skeleton shape="card" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <SectionEmptyState
        headline="Nothing new"
        body="We'll tell you when a round locks or a pool needs you."
      />
    );
  }

  const groups = groupByDay({ items, now, timeZone: resolveViewerTimeZone() });

  return (
    <div className="flex flex-col gap-4">
      {groups.map((group) => (
        <div key={group.header} className="flex flex-col gap-1">
          <h2 className="text-caption text-ink-tertiary">{group.header}</h2>
          {group.items.map((item) => (
            <NotificationRow
              key={item.inboxItemId}
              item={item}
              now={now}
              onOpen={onOpen}
            />
          ))}
        </div>
      ))}
    </div>
  );
};
